import {
  Badge,
  Box,
  HStack,
  Icon,
  Progress,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import {
  KYDAdminEventSummary,
  KYDEventStatus,
} from "@common/interfaces/KYDEvent";
import { useInfiniteHits } from "react-instantsearch";
import EventFilters from "./EventFilters";
import { Link } from "react-router-dom";
import { colorForPercentage, formatEventDateWithYear } from "../../utils";
import LoadingEmptyResults from "./LoadingEmptyResults";
import { TriangleDownIcon } from "@chakra-ui/icons";
import { TriangleUpIcon } from "@chakra-ui/icons";
import InfiniteScroll from "./InfiniteScroll";
import { FaHourglassHalf } from "react-icons/fa";

export const colorForStatus = (status: KYDEventStatus): string => {
  switch (status) {
    case "ARCHIVED":
      return "gray";
    case "CANCELED":
      return "red";
    case "DRAFT":
      return "blue";
    case "PENDING_APPROVAL":
    case "PENDING_ANNOUNCEMENT":
      return "yellow";
    case "OPEN":
      return "green";
    case "OFFSALE":
      return "red";
    default:
      return "gray";
  }
};

export const badgeForStatus = (status: KYDEventStatus) => {
  const bg = colorForStatus(status);
  let name = "N/A";

  switch (status) {
    case "ARCHIVED":
      name = "ARCHIVED";
      break;
    case "CANCELED":
      name = "CANCELED";
      break;
    case "DRAFT":
      name = "Draft";
      break;
    case "PENDING_APPROVAL":
      name = "Pending Approval";
      break;
    case "PENDING_ANNOUNCEMENT":
      name = "Unannounced";
      break;
    case "OPEN":
      name = "On Sale";
      break;
    case "OFFSALE":
      name = "Off Sale";
      break;
    default: {
    }
  }

  return (
    <Badge justifyItems={"center"} colorScheme={bg}>
      {name}
    </Badge>
  );
};

export const EventsTable = ({ onChangeSort, sortOrder, ...props }) => {
  const { items, isLastPage, showMore } = useInfiniteHits<KYDAdminEventSummary>(
    {
      escapeHTML: false,
      showPrevious: false,
    }
  );

  const handleSort = () => {
    onChangeSort(sortOrder === "ascending" ? "descending" : "ascending");
  };

  const calculatePercentage = (portion: number, total: number) => {
    if (!portion || !total) return 0;

    return (portion * 100) / total;
  };

  return (
    <VStack w="full">
      <EventFilters onChangeSort={onChangeSort} />
      <Table {...props}>
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Status</Th>
            <Th cursor="pointer" onClick={() => handleSort()}>
              <HStack spacing="1">
                <Text>Date</Text>
                <Box w="3" h="3" display="inline-flex" alignItems="center">
                  <Icon
                    as={
                      sortOrder === "ascending"
                        ? TriangleUpIcon
                        : TriangleDownIcon
                    }
                    aria-label={`sorted ${sortOrder}`}
                    boxSize="3"
                  />
                </Box>
              </HStack>
            </Th>
            <Th>Venue</Th>
            <Th>Sales</Th>
            <Th>Tickets</Th>
          </Tr>
        </Thead>
        <Tbody>
          {items.map((kydEvent) => (
            <Tr key={kydEvent.objectID}>
              <Td>
                <Box>
                  <HStack spacing="3">
                    <Link to={`/events/${kydEvent.id}`}>
                      <Box>
                        <Text fontWeight="medium">{kydEvent.name}</Text>
                      </Box>
                    </Link>
                  </HStack>
                  {kydEvent.is_external ? (
                    <Badge mt={2} colorScheme="blue">
                      External
                    </Badge>
                  ) : null}
                </Box>
              </Td>
              <Td>{badgeForStatus(kydEvent.status)}</Td>
              <Td>
                <Text color="muted">
                  {formatEventDateWithYear(
                    kydEvent.start_at,
                    kydEvent.timezone
                  )}
                </Text>
              </Td>
              <Td>
                <Text color="muted">{kydEvent.venue?.name}</Text>
              </Td>
              <Td>
                <VStack spacing={1} alignItems={"flex-start"}>
                  <Text>
                    {kydEvent.realtime_metrics
                      ? kydEvent.realtime_metrics.sales_display
                      : kydEvent.total_gmv_display}
                  </Text>
                  <Tooltip label={"Number of tickets on waitlist"}>
                    <Box
                      className="flex"
                      hidden={
                        !kydEvent.realtime_metrics ||
                        !kydEvent.realtime_metrics.waitlist_buy_tickets ||
                        kydEvent.realtime_metrics?.waitlist_buy_tickets === 0
                      }
                    >
                      <HStack opacity={0.5} flexShrink={0}>
                        <FaHourglassHalf fontSize="0.6rem" />
                        <Text fontSize={"x-small"}>
                          {
                            kydEvent.realtime_metrics
                              ?.waitlist_buy_tickets_display
                          }
                        </Text>
                      </HStack>
                    </Box>
                  </Tooltip>
                </VStack>
              </Td>
              <Td>
                {kydEvent.realtime_metrics ? (
                  <VStack>
                    <HStack>
                      <Text>
                        {kydEvent.realtime_metrics
                          ? kydEvent.realtime_metrics.tickets_display
                          : kydEvent.total_tickets_purchased_display}
                      </Text>
                      <Text>/</Text>
                      <Text>
                        {kydEvent.realtime_metrics
                          ? kydEvent.realtime_metrics.quantity_display
                          : kydEvent.total_tickets_purchased}
                      </Text>
                    </HStack>
                    <VStack w="full" spacing={0} pt={1}>
                      <Progress
                        size="sm"
                        w="full"
                        rounded={"md"}
                        colorScheme={colorForPercentage(
                          calculatePercentage(
                            kydEvent?.realtime_metrics?.tickets,
                            kydEvent?.realtime_metrics?.quantity
                          )
                        )}
                        value={calculatePercentage(
                          kydEvent?.realtime_metrics?.tickets,
                          kydEvent?.realtime_metrics?.quantity
                        )}
                      />
                    </VStack>
                  </VStack>
                ) : (
                  <Text>{kydEvent.total_tickets_purchased}</Text>
                )}
              </Td>
            </Tr>
          ))}
          <LoadingEmptyResults />
        </Tbody>
      </Table>
      <InfiniteScroll
        hasMore={!isLastPage}
        loadMore={showMore}
        threshold={300} // Load more items when user is 300px from bottom
        preloadBatch={false} // Add this to preload the next batch
      />
    </VStack>
  );
};
